import React, { useMemo } from 'react';

import {
  Stack,
} from '@mui/material';

import {
  useGetLessonsQuery,
} from '../../../services/classes';
import { Class, Lesson } from '../../../services/classes/types';
import { useGetStreamQuery } from '../../../services/streams';
import { Metadata } from '../../../services/types';
import { getLessonCourse } from '../../../utils/common/classes';

import ClassCard from './ClassCard';
import LessonsCard from './LessonsCard';
import ScheduleCard from './ScheduleCard';
import StudentsCard from './StudentsCard';
import WaitlistCard from './WaitlistCard';

interface DetailsTabProps {
  classObj: Class,
  metadata?: Metadata,
  setSelectedLesson: (lesson?: Lesson) => void,
  selectedLesson?: Lesson,
}

interface CreditCounter {
  termCounter: number;
  weekCounter: number;
}

const DetailsTab = ({
  classObj,
  metadata,
  setSelectedLesson,
  selectedLesson,
}: DetailsTabProps) => {
  const { data: lessons, refetch } = useGetLessonsQuery({
    subject: classObj.subject, classId: classObj.classId,
  });

  const deselectLesson = () => {
    setSelectedLesson(undefined);
  };

  const handleSelect = (lesson: Lesson) => {
    if (selectedLesson?.id === lesson.id) {
      deselectLesson();
    } else {
      setSelectedLesson(lesson);
    }
  };

  const { data: stream } = useGetStreamQuery(
    { course: classObj.course, streamId: classObj.stream });

  // Map lessons to include term and week counts
  const mappedLessons = useMemo(() => {
    // Initialise credit counter
    const creditCount: CreditCounter = {
      termCounter: stream?.data.terms ?? 0,
      weekCounter: ((stream?.data.creditsPerTerm ?? 0) + 1),
    };

    return lessons?.data.map((_, i, arr) => {
      const lesson = arr[arr.length - 1 - i];
      if (getLessonCourse(lesson) === classObj.course) {
        // Only label lessons belonging to the current class course

        if (creditCount.weekCounter <= 1) {
          creditCount.termCounter--;
          creditCount.weekCounter = stream?.data.creditsPerTerm ?? 0;
        } else if (creditCount.weekCounter > 1) {
          creditCount.weekCounter--;
        }

        if (creditCount.termCounter >= 1 && creditCount.weekCounter >= 1) {
          // Only pass in term and week if it's a valid number above 1
          return {
            ...lesson,
            term: creditCount.termCounter,
            week: creditCount.weekCounter,
          };
        }
      }

      return lesson;
    }).reverse();
  }, [lessons, stream]);

  return (
    <Stack direction="column" spacing={2}>
      <ClassCard
        classObj={classObj}
        classObjMetadata={metadata}
        lessons={mappedLessons}
      />
      <StudentsCard classObj={classObj} lessons={mappedLessons} />
      <WaitlistCard classObj={classObj} />
      <ScheduleCard
        classObj={classObj}
        lessons={mappedLessons}
      />
      <LessonsCard
        lessons={mappedLessons}
        lessonMetadata={lessons?.metadata}
        onSelectLesson={handleSelect}
        selectedLesson={selectedLesson}
        classObj={classObj}
        refetchLessons={refetch}
      />
    </Stack>
  );
};
export default DetailsTab;
